import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Hash,
  BarChart3,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  TrendingUp,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
} from 'lucide-react';
import TikTokIcon from '../ui/TikTokIcon';
import { Card } from '../ui';
import { socialMediaService } from '@/services/socialMediaService';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { DiscoveredHashtag } from '@/types/attributionTag';

interface HashtagPost {
  postId: string;
  platformPostId: string;
  platformId: number;
  platformName: string;
  platformDisplayName: string;
  username: string;
  displayName: string;
  postedAt: string;
  postType: string;
  contentType: string;
  isSponsored: boolean;
  permalink: string;
  postUrl?: string;
  caption: string;
  hashtags: Array<{
    hashtag: string;
    position: number;
  }>;
  stats?: {
    emv: string;
    likesCount: number;
    viewsCount: number;
    commentsCount: number;
    sharesCount: number;
    savesCount: number;
    playsCount: number;
  };
  metrics?: {
    emv: string;
    likes: number;
    views: number;
    comments: number;
    shares: number;
  };
  platform?: {
    name: string;
  };
}

interface HashtagMetrics {
  hashtag: string;
  postCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalEmv: string;
  totalEmvValue: number;
  avgEmvPerPost: string;
  avgViewsPerPost: number;
  avgLikesPerPost: number;
  avgCommentsPerPost: number;
  avgSharesPerPost: number;
}

interface DateRange {
  start: string;
  end: string;
}

interface DiscoveredHashtagInsightsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  hashtagId: number | null;
  hashtag: string | null;
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  customDateRange?: DateRange | null;
  onPromoteClick?: (hashtag: DiscoveredHashtag) => void;
}

const DiscoveredHashtagInsightsDrawer: React.FC<DiscoveredHashtagInsightsDrawerProps> = ({
  isOpen,
  onClose,
  hashtagId,
  hashtag,
  period = '90days',
  customDateRange,
  onPromoteClick,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'posts'>('overview');
  const [hashtagPosts, setHashtagPosts] = useState<HashtagPost[]>([]);
  const [hashtagMetrics, setHashtagMetrics] = useState<HashtagMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);

  const { selectedCustomer } = useGlobalCustomerStore();

  // Load hashtag details and posts when hashtagId changes
  useEffect(() => {
    if (hashtagId && selectedCustomer?.id && isOpen) {
      loadHashtagData();
      loadHashtagPosts();
    }
  }, [hashtagId, selectedCustomer?.id, isOpen, period, customDateRange]);

  const loadHashtagData = async () => {
    if (!hashtag || !selectedCustomer?.id) return;

    try {
      setLoading(true);
      const response = await socialMediaService.getHashtagAnalytics({
        customerId: selectedCustomer.id,
        period: period,
        limit: 1000,
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });

      if (response.data?.hashtags) {
        const hashtagData = response.data.hashtags.find((h: any) => h.hashtag === hashtag);
        if (hashtagData) {
          const current = hashtagData.current as any;
          setHashtagMetrics({
            hashtag: hashtag,
            postCount: current.postCount,
            totalViews: current.totalViews,
            totalLikes: current.totalLikes,
            totalComments: current.totalComments,
            totalShares: current.totalShares,
            totalEmv: current.totalEmvFormatted || current.totalEmv,
            totalEmvValue:
              typeof current.totalEmv === 'string'
                ? parseFloat(current.totalEmv.replace(/[$,]/g, '')) || 0
                : parseFloat(current.totalEmv) || 0,
            avgEmvPerPost:
              current.avgEmvPerPostFormatted || current.avgEmvPerPost,
            avgViewsPerPost: current.avgViewsPerPost,
            avgLikesPerPost: current.avgLikesPerPost,
            avgCommentsPerPost: current.avgCommentsPerPost,
            avgSharesPerPost: current.avgSharesPerPost,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load hashtag data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHashtagPosts = async () => {
    if (!hashtagId || !selectedCustomer?.id) return;

    try {
      setPostsLoading(true);
      const response = await socialMediaService.getHashtagPosts({
        customerId: selectedCustomer.id,
        hashtagId: hashtagId,
        period: period,
        limit: 20,
        sortBy: 'emv',
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });

      if (response.data?.posts) {
        setHashtagPosts(response.data.posts);
      }
    } catch (error) {
      console.error('Failed to load hashtag posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  // Get platform icon
  const getPlatformIcon = (platformName: string | undefined) => {
    if (!platformName) return Instagram;
    const lowerPlatform = platformName.toLowerCase();
    switch (lowerPlatform) {
      case 'instagram':
        return Instagram;
      case 'tiktok':
        return TikTokIcon;
      case 'facebook':
        return Facebook;
      case 'twitter':
      case 'x':
        return Twitter;
      case 'youtube':
        return Youtube;
      case 'linkedin':
        return Linkedin;
      default:
        return Instagram;
    }
  };

  // Get platform color
  const getPlatformColor = (platformName: string | undefined) => {
    if (!platformName) return 'text-gray-500';
    const lowerPlatform = platformName.toLowerCase();
    switch (lowerPlatform) {
      case 'instagram':
        return 'text-pink-500';
      case 'tiktok':
        return 'text-black';
      case 'facebook':
        return 'text-blue-600';
      case 'twitter':
      case 'x':
        return 'text-blue-400';
      case 'youtube':
        return 'text-red-600';
      case 'linkedin':
        return 'text-blue-700';
      default:
        return 'text-gray-500';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateCaption = (caption: string, maxLength: number = 150) => {
    if (!caption) return '';
    if (caption.length <= maxLength) return caption;
    return caption.substring(0, maxLength) + '...';
  };

  const handlePromoteClick = () => {
    if (onPromoteClick && hashtagId && hashtag && hashtagMetrics) {
      const discoveredHashtag: DiscoveredHashtag = {
        hashtag_id: hashtagId,
        hashtag_text: hashtag,
        description: null,
        status: null,
        post_count: hashtagMetrics.postCount,
        total_emv: hashtagMetrics.totalEmvValue,
        total_engagement:
          hashtagMetrics.totalLikes + hashtagMetrics.totalComments + hashtagMetrics.totalShares,
        latest_post_date: null,
        earliest_post_date: null,
        is_tracked: false,
        account_count: 0,
      };
      onPromoteClick(discoveredHashtag);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <Hash className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">#{hashtag}</h2>
                  <p className="text-sm text-gray-500">Discovered Hashtag Analytics</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {onPromoteClick && (
                  <button
                    onClick={handlePromoteClick}
                    className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                    Promote to Attribution Tag
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'posts'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Top Posts ({hashtagPosts.length})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-xs text-gray-900">Loading hashtag details...</span>
                  </div>
                </div>
              )}

              {!loading && activeTab === 'overview' && hashtagMetrics && (
                <div className="p-6 space-y-4">
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Total Posts</p>
                          <p className="text-base font-bold text-gray-900">
                            {formatNumber(hashtagMetrics.postCount)}
                          </p>
                        </div>
                        <div className="p-1.5 bg-blue-100 rounded-md">
                          <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Total EMV</p>
                          <p className="text-base font-bold text-green-600">
                            {hashtagMetrics.totalEmv}
                          </p>
                        </div>
                        <div className="p-1.5 bg-green-100 rounded-md">
                          <DollarSign className="h-3.5 w-3.5 text-green-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Total Views</p>
                          <p className="text-base font-bold text-gray-900">
                            {formatNumber(hashtagMetrics.totalViews)}
                          </p>
                        </div>
                        <div className="p-1.5 bg-purple-100 rounded-md">
                          <Eye className="h-3.5 w-3.5 text-purple-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Total Likes</p>
                          <p className="text-base font-bold text-gray-900">
                            {formatNumber(hashtagMetrics.totalLikes)}
                          </p>
                        </div>
                        <div className="p-1.5 bg-red-100 rounded-md">
                          <Heart className="h-3.5 w-3.5 text-red-600" />
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Average Post Performance */}
                  <Card className="px-4 py-3 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200">
                    <h3 className="text-xs font-semibold text-gray-900 mb-3">
                      Average Post Performance
                    </h3>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-green-100">
                        <div className="flex items-center justify-center mb-1">
                          <DollarSign className="h-3.5 w-3.5 text-green-500" />
                        </div>
                        <p className="text-xs font-bold text-green-600 text-center">
                          {hashtagMetrics.avgEmvPerPost}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">EMV</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-purple-100">
                        <div className="flex items-center justify-center mb-1">
                          <Eye className="h-3.5 w-3.5 text-purple-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {formatNumber(Math.round(hashtagMetrics.avgViewsPerPost))}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Views</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-red-100">
                        <div className="flex items-center justify-center mb-1">
                          <Heart className="h-3.5 w-3.5 text-red-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {formatNumber(Math.round(hashtagMetrics.avgLikesPerPost))}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Likes</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-blue-100">
                        <div className="flex items-center justify-center mb-1">
                          <MessageCircle className="h-3.5 w-3.5 text-blue-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {formatNumber(Math.round(hashtagMetrics.avgCommentsPerPost))}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Comments</p>
                      </div>
                    </div>
                  </Card>

                  {/* Engagement Breakdown */}
                  <Card className="px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-900 mb-2">
                      Engagement Breakdown
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <MessageCircle className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-xs font-medium text-gray-700">Comments</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {formatNumber(hashtagMetrics.totalComments)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Share2 className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs font-medium text-gray-700">Shares</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {formatNumber(hashtagMetrics.totalShares)}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {!loading && activeTab === 'posts' && (
                <div className="p-6">
                  {postsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-xs text-gray-900">Loading posts...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[...hashtagPosts].sort((a, b) => {
                        const emvA = Number(a.metrics?.emv || a.stats?.emv || 0);
                        const emvB = Number(b.metrics?.emv || b.stats?.emv || 0);
                        return emvB - emvA;
                      }).map((post, index) => {
                        const platformName =
                          post.platform?.name || post.platformName || 'instagram';
                        const PlatformIcon = getPlatformIcon(platformName);
                        const postUrl = post.postUrl || post.permalink;
                        const emv = post.metrics?.emv || post.stats?.emv || '$0';
                        const views =
                          post.metrics?.views || post.stats?.viewsCount || 0;
                        const likes =
                          post.metrics?.likes || post.stats?.likesCount || 0;
                        const comments =
                          post.metrics?.comments || post.stats?.commentsCount || 0;
                        const shares =
                          post.metrics?.shares || post.stats?.sharesCount || 0;

                        return (
                          <motion.div
                            key={post.postId}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div
                                    className={`p-2 rounded-lg ${getPlatformColor(platformName)}`}
                                  >
                                    <PlatformIcon className="h-5 w-5" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-xs font-medium text-gray-900">
                                        {post.username}
                                      </p>
                                      <span className="text-xs font-bold text-gray-700 capitalize">
                                        {post.postType}
                                      </span>
                                      {post.isSponsored && (
                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                                          Sponsored
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-bold text-green-600">
                                        {emv}
                                      </span>
                                      {postUrl && (
                                        <a
                                          href={postUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-600 mb-3">
                                    {truncateCaption(post.caption)}
                                  </p>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-4">
                                      <div className="flex items-center space-x-1">
                                        <Eye className="h-3 w-3" />
                                        <span>{formatNumber(views)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Heart className="h-3 w-3" />
                                        <span>{formatNumber(likes)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <MessageCircle className="h-3 w-3" />
                                        <span>{formatNumber(comments)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Share2 className="h-3 w-3" />
                                        <span>{formatNumber(shares)}</span>
                                      </div>
                                    </div>
                                    <span>{formatDate(post.postedAt)}</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                      {hashtagPosts.length === 0 && (
                        <div className="text-center py-12">
                          <Hash className="mx-auto h-10 w-10 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No posts found</h3>
                          <p className="mt-1 text-xs text-gray-500">
                            No posts found for this hashtag in the selected period.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DiscoveredHashtagInsightsDrawer;
